/* eslint-disable no-nested-ternary */

import * as React from 'react';
import cn from 'classnames';

import Text from 'crehana-ui/Text';
import { purple } from 'crehana-ui/styles/colors';

import ArrowDown from 'crehana-icons/ArrowDown';
import Check from 'crehana-icons/CheckNew';

import useOnClickOutside from './useOnClickOutside';
import { Triangle } from './common';
import OptionDialog from './OptionDialog';

type OptionType = {
  value: string;
  label: string;
};

type DropdownProps = {
  options: OptionType[];
  max?: boolean;
  label: string;
  showArrow?: boolean;
  className?: string;
  selected: OptionType[] | [];
  onSelected(opt: OptionType[]): void;
};

const Dropdown: React.FC<DropdownProps> = ({
  options,
  max,
  label,
  showArrow = true,
  selected = [],
  singleSelect,
  onSelected,
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isClikedOutside = useOnClickOutside(containerRef);

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    action: () => void,
  ) => {
    if (event.keyCode === 13) {
      event.stopPropagation();
      action();
    }
  };

  const onToggleOpen = () => {
    setOpen(!open);
  };

  const handleClick = () => {
    if (open) {
      onToggleOpen();
    }
  };

  const checkIfOptionIsSelected = (opt: OptionType) =>
    selected.filter(sel => sel.value === opt.value).length > 0;

  const onSelect = (option: OptionType) => {
    const newSelected = option;

    if (singleSelect) {
      onSelected(option);
    } else if (typeof onSelected === 'function') {
      if (checkIfOptionIsSelected(newSelected)) {
        onSelected(selected.filter(sel => sel.value !== newSelected.value));
      } else {
        onSelected([...selected, newSelected]);
      }
    }
  };

  React.useEffect(() => {
    if (isClikedOutside) {
      if (max) {
        if (!openDialog) {
          handleClick();
        }
      } else {
        handleClick();
      }
    }
  }, [isClikedOutside, openDialog]);

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      <div
        className="flex items-center cursor-pointer"
        onClick={onToggleOpen}
        onKeyPress={onToggleOpen}
      >
        <Text type="subtitle3" color={open ? 'purple-main' : 'base-main'}>
          {label}
          {selected && selected.length > 0 && (
            <span className="ml-8">{`(${selected.length})`}</span>
          )}
        </Text>
        {showArrow && (
          <ArrowDown className="ml-16" size={12} color={open ? purple : ''} />
        )}
      </div>
      {open ? (
        max ? (
          <OptionDialog
            onSelected={onSelected}
            selected={selected}
            positionRef={containerRef}
            handleKeyDown={handleKeyDown}
            setOpenDialog={setOpenDialog}
            onToggleOpen={onToggleOpen}
            options={options}
          />
        ) : (
          <div
            className="flex flex-col absolute bg-white shadow-dropdown p-32"
            css={`
              top: 42px;
              transform: translateX(-50%);
              left: 50%;
              min-width: 193px;
              z-index: 99;
            `}
          >
            <Triangle />
            {options.map((option, i) => (
              <div
                key={`${label}-option-${option.value}`}
                onClick={() => onSelect(option)}
                onKeyDown={e => handleKeyDown(e, () => onSelect(option))}
                className={`flex items-center justify-between cursor-pointer ${
                  options.length > i + 1 ? 'mb-16' : ''
                }`}
              >
                <Text
                  className={checkIfOptionIsSelected(option) ? 'font-bold' : ''}
                  type="body4"
                  color={
                    checkIfOptionIsSelected(option)
                      ? 'purple-main'
                      : 'base-main'
                  }
                >
                  {option.label}
                </Text>
                {checkIfOptionIsSelected(option) && (
                  <Check size={16} color={purple} />
                )}
              </div>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
};

export default Dropdown;
